/**
 * Persistência do recorte do cabeçalho — empresa e competência (SPEC-017, decisões 11 e 13).
 *
 * Único lugar do projeto que lê/grava estas chaves, no molde de `@/auth/session-store`.
 *
 * O recorte mora em DOIS storages, porque as duas decisões pedem durações diferentes:
 *
 * - a **competência** (modo, mês, ano e as datas) fica em `localStorage`: a decisão 13 diz
 *   que ela vale "até a pessoa trocar, também em outra aba e no próximo acesso, no mesmo
 *   navegador";
 * - a **empresa** e o "já buscado" continuam em `sessionStorage`, como na decisão 11
 *   ("até sair do sistema"). A decisão 13 não os amplia, e levá-los a `localStorage` faria o
 *   próximo login — de outra pessoa, ou de outro gestor — abrir com a empresa da sessão
 *   anterior e as telas buscando sozinhas.
 *
 * NÃO é dado sensível: é a escolha de tela da pessoa.
 */

import { MESES, formatYmdLocal } from '@/lib/utils/formatters';

export interface RecorteDoCabecalho {
  empresaId: string | null;
  empresaCnpjs: string | null;
  dataInicio: string | null;
  dataFim: string | null;
  periodoModo: 'mesano' | 'periodo';
  periodoMes: string | null;
  periodoAno: number | null;
  /**
   * Houve um "Buscar" com este recorte.
   *
   * É ELE que liga a decisão 11, e não a simples existência de um recorte: enquanto a
   * pessoa escolhe empresa e período no cabeçalho, os valores mudam a cada clique, e abrir
   * tela por tela buscando a cada mudança seria uma consulta por clique. Depois do primeiro
   * Buscar, as outras telas abrem já buscadas — que é o que a decisão pede.
   */
  aplicado: boolean;
}

export type CompetenciaDoCabecalho = Pick<
  RecorteDoCabecalho,
  'dataInicio' | 'dataFim' | 'periodoModo' | 'periodoMes' | 'periodoAno'
>;

type EmpresaDoCabecalho = Pick<RecorteDoCabecalho, 'empresaId' | 'empresaCnpjs' | 'aplicado'>;

/*
  A chave da empresa é NOVA de propósito. A antiga (`tp_recorte_cabecalho`) guardava o recorte
  inteiro, com "últimos 30 dias" e `aplicado`; relida depois do deploy, numa aba aberta, ela
  ligaria a busca automática com a competência nova, que ninguém mandou buscar.
*/
const CHAVE_EMPRESA = 'tp_empresa_cabecalho';
const CHAVE_COMPETENCIA = 'tp_competencia_cabecalho';

/** A competência do mês de `hoje`, no modo Mês/Ano — o que o primeiro acesso mostra (decisão 13a). */
export function competenciaPadrao(hoje: Date = new Date()): CompetenciaDoCabecalho {
  const primeiro = new Date(hoje.getFullYear(), hoje.getMonth(), 1);
  const ultimo = new Date(hoje.getFullYear(), hoje.getMonth() + 1, 0);
  return {
    dataInicio: formatYmdLocal(primeiro),
    dataFim: formatYmdLocal(ultimo),
    periodoModo: 'mesano',
    periodoMes: MESES[hoje.getMonth()] ?? null,
    periodoAno: hoje.getFullYear(),
  };
}

/**
 * A competência que um par de datas digitadas forma.
 *
 * Quem edita as datas uma a uma (o cabeçalho próprio da NFS-e) não escolhe modo. Se as duas
 * fecham um mês inteiro, é a competência daquele mês; senão, é um período. Sem isso o botão
 * diria "11/2026" sobre datas que já não são novembro, e as telas que só aceitam Mês/Ano
 * consultariam um recorte diferente do que o botão anuncia.
 */
export function competenciaDasDatas(
  dataInicio: string | null,
  dataFim: string | null
): CompetenciaDoCabecalho {
  const ini = dataInicio ? /^(\d{4})-(\d{2})-01$/.exec(dataInicio) : null;
  if (ini && dataFim) {
    const mes = competenciaPadrao(new Date(Number(ini[1]), Number(ini[2]) - 1, 1));
    if (mes.dataInicio === dataInicio && mes.dataFim === dataFim) return mes;
  }
  return { dataInicio, dataFim, periodoModo: 'periodo', periodoMes: null, periodoAno: null };
}

const EMPRESA_PADRAO: EmpresaDoCabecalho = {
  empresaId: null,
  empresaCnpjs: null,
  aplicado: false,
};

export function recortePadrao(): RecorteDoCabecalho {
  return { ...EMPRESA_PADRAO, ...competenciaPadrao() };
}

/**
 * O recorte guardado, ou o padrão, peça por peça.
 *
 * <b>A FORMA é conferida, não suposta.</b> O que está no storage foi escrito por uma
 * versão anterior do app e sobrevive a deploy — a competência, agora, sobrevive até a
 * fechar o navegador. Um `periodoModo` que deixou de existir, ou um campo que virou outro
 * tipo, entraria aqui e viajaria para as cinco telas como se fosse escolha da pessoa, e o
 * sintoma seria consulta vazia em todo lugar, sem erro. Fora do contrato, a peça é
 * descartada inteira e o padrão dela vale.
 *
 * Ler storage também PODE LANÇAR (aba anônima, cookies bloqueados): por isso o
 * `try/catch`, e por isso ele devolve o padrão em vez de deixar o app sem cabeçalho.
 */
export function recorteGuardado(): RecorteDoCabecalho {
  return { ...empresaGuardada(), ...competenciaGuardada() };
}

function empresaGuardada(): EmpresaDoCabecalho {
  const lido = lerJson(() => sessionStorage, CHAVE_EMPRESA);
  return ehEmpresa(lido) ? soEmpresa(lido) : EMPRESA_PADRAO;
}

/**
 * A competência escolhida, ou a do mês corrente.
 *
 * Quando o mês vira, a guardada continua valendo (decisão 13c): ela tem as datas, e não um
 * "mês corrente" que andaria sozinho.
 */
export function competenciaGuardada(): CompetenciaDoCabecalho {
  const lido = lerJson(() => localStorage, CHAVE_COMPETENCIA);
  return ehCompetencia(lido) ? soCompetencia(lido) : competenciaPadrao();
}

/** Grava a empresa e o "já buscado" da aba. A competência tem a sua própria gravação. */
export function guardarRecorte(recorte: RecorteDoCabecalho): void {
  gravarJson(() => sessionStorage, CHAVE_EMPRESA, soEmpresa(recorte));
}

/**
 * Grava a competência para as outras abas e o próximo acesso.
 *
 * É separada de `guardarRecorte` porque só a ESCOLHA da pessoa se grava. Gravar o que a
 * tela abriu mostrando faria o mês corrente de hoje virar "a escolha" de quem nunca escolheu,
 * e no mês seguinte ela abriria presa no mês anterior.
 */
export function guardarCompetencia(competencia: CompetenciaDoCabecalho): void {
  gravarJson(() => localStorage, CHAVE_COMPETENCIA, soCompetencia(competencia));
}

/** Volta a seguir o mês corrente — é o "Limpar filtros". */
export function esquecerCompetencia(): void {
  try {
    localStorage.removeItem(CHAVE_COMPETENCIA);
  } catch {
    /* Nada a esquecer se nem dá para ler. */
  }
}

export function limparRecorte(): void {
  try {
    sessionStorage.removeItem(CHAVE_EMPRESA);
  } catch {
    /* Nada a limpar se nem dá para ler. */
  }
  esquecerCompetencia();
}

function lerJson(storage: () => Storage, chave: string): unknown {
  try {
    const cru = storage().getItem(chave);
    return cru ? (JSON.parse(cru) as unknown) : null;
  } catch {
    return null;
  }
}

function gravarJson(storage: () => Storage, chave: string, valor: unknown): void {
  try {
    storage().setItem(chave, JSON.stringify(valor));
  } catch {
    /* Storage indisponível: o recorte segue valendo na memória da página. */
  }
}

function soEmpresa(r: EmpresaDoCabecalho): EmpresaDoCabecalho {
  return { empresaId: r.empresaId, empresaCnpjs: r.empresaCnpjs, aplicado: r.aplicado };
}

function soCompetencia(r: CompetenciaDoCabecalho): CompetenciaDoCabecalho {
  return {
    dataInicio: r.dataInicio,
    dataFim: r.dataFim,
    periodoModo: r.periodoModo,
    periodoMes: r.periodoMes,
    periodoAno: r.periodoAno,
  };
}

function textoOuNulo(v: unknown): boolean {
  return v === null || typeof v === 'string';
}

function ehData(v: unknown): boolean {
  return typeof v === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(v);
}

function comoObjeto(v: unknown): Record<string, unknown> | null {
  return typeof v === 'object' && v !== null ? (v as Record<string, unknown>) : null;
}

function ehEmpresa(v: unknown): v is EmpresaDoCabecalho {
  const r = comoObjeto(v);
  return (
    !!r &&
    textoOuNulo(r.empresaId) &&
    textoOuNulo(r.empresaCnpjs) &&
    typeof r.aplicado === 'boolean'
  );
}

/*
  A competência guardada tem de ter as duas datas: sem elas as telas consultariam um período
  vazio. E no modo Mês/Ano, o mês e o ano, que são o que o botão mostra.
*/
function ehCompetencia(v: unknown): v is CompetenciaDoCabecalho {
  const r = comoObjeto(v);
  if (!r || !ehData(r.dataInicio) || !ehData(r.dataFim)) return false;

  if (r.periodoModo === 'mesano') {
    return (
      (MESES as readonly unknown[]).includes(r.periodoMes) &&
      typeof r.periodoAno === 'number' &&
      Number.isInteger(r.periodoAno)
    );
  }

  return (
    r.periodoModo === 'periodo' &&
    textoOuNulo(r.periodoMes) &&
    (r.periodoAno === null || typeof r.periodoAno === 'number')
  );
}
